import { Link } from 'react-router-dom'
import SEO, { breadcrumbs } from '../components/common/SEO'
import PageHero from '../components/common/PageHero'
import ResponsiveImage from '../components/common/ResponsiveImage'
import {
  Breadcrumbs,
  BookButton,
  ProseSections,
  BulletList,
  SideCard,
  PractitionerGrid,
  FaqSection,
  LinkCards,
  BookingBand,
} from '../components/detail/DetailBlocks'
import { conditionBySlug } from '../data/conditions'
import { serviceBySlug } from '../data/servicePages'
import { SITE_URL } from '../data/site'
import { faqSchema } from '../data/faqs'
import NotFound from './NotFound'

const ConditionPage = ({ slug }) => {
  const condition = conditionBySlug(slug)
  if (!condition) return <NotFound />

  const path = `/conditions/${condition.slug}`
  const trail = [
    { name: 'Home', path: '/' },
    { name: 'Conditions', path: '/conditions' },
    { name: condition.name, path },
  ]
  const services = condition.services.map(serviceBySlug).filter(Boolean)
  const related = condition.related.map(conditionBySlug).filter(Boolean)

  return (
    <>
      <SEO
        title={condition.title}
        description={condition.description}
        path={path}
        image={condition.image}
        imageAlt={condition.imageAlt}
        noIndex={!condition.reviewed}
        structuredData={[
          breadcrumbs(trail),
          {
            '@context': 'https://schema.org',
            '@type': 'MedicalWebPage',
            name: condition.h1,
            url: `${SITE_URL}${path}`,
            description: condition.description,
            about: {
              '@type': 'MedicalCondition',
              name: condition.name,
              signOrSymptom: condition.symptoms.map((s) => ({
                '@type': 'MedicalSignOrSymptom',
                name: s,
              })),
              possibleTreatment: services.map((s) => ({
                '@type': 'MedicalTherapy',
                name: s.name,
                url: `${SITE_URL}/services/${s.slug}`,
              })),
            },
            publisher: { '@id': `${SITE_URL}/#business` },
          },
          faqSchema(condition.faqs),
        ]}
      />

      <div className="bg-cream-200">
        <PageHero title={condition.h1} subtitle="Conditions we treat" texture="terracotta-sand" size="sm">
          <p className="font-sans text-dark-600 text-base md:text-lg leading-relaxed max-w-2xl mx-auto mb-8">
            {condition.intro}
          </p>
          <BookButton />
        </PageHero>
      </div>

      <div className="bg-cream-200">
        <Breadcrumbs items={trail} />
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
          <div className="grid lg:grid-cols-[1fr_380px] gap-10 lg:gap-16 items-start">
            <div>
              <div className="rounded-[20px] overflow-hidden shadow-lg mb-10">
                <ResponsiveImage
                  src={condition.image}
                  alt={condition.imageAlt}
                  sizes="(min-width: 1024px) 700px, 100vw"
                  className="w-full aspect-[16/9] object-cover"
                />
              </div>
              <ProseSections sections={condition.sections} />
            </div>

            <aside className="space-y-6 lg:sticky lg:top-28">
              <SideCard>
                <h2 className="font-display text-dark-700 text-xl font-semibold mb-4">
                  {condition.symptomsTitle}
                </h2>
                <BulletList items={condition.symptoms} />
              </SideCard>
              <SideCard>
                <h2 className="font-display text-dark-700 text-xl font-semibold mb-4">
                  How we treat it
                </h2>
                <ul className="space-y-3">
                  {services.map((s) => (
                    <li key={s.slug}>
                      <Link
                        to={`/services/${s.slug}`}
                        className="font-sans text-base text-brand-orange-600 hover:text-brand-orange-500 underline underline-offset-4"
                      >
                        {s.name}
                      </Link>
                    </li>
                  ))}
                </ul>
                <BookButton className="mt-6 w-full text-center" />
              </SideCard>
              <SideCard className="bg-cream-100">
                <h2 className="font-display text-dark-700 text-base font-semibold mb-2">
                  When to see a doctor
                </h2>
                <p className="font-sans text-dark-500 text-sm leading-relaxed">
                  {condition.whenToSeeDoctor}
                </p>
              </SideCard>
            </aside>
          </div>
        </section>
      </div>

      <PractitionerGrid slugs={condition.practitioners} heading="Practitioners who treat this" />
      <FaqSection faqs={condition.faqs} heading={`${condition.name} FAQs`} />
      <LinkCards
        label="Related conditions"
        heading="Other conditions we treat"
        items={related.map((c) => ({
          to: `/conditions/${c.slug}`,
          name: c.name,
          description: c.intro,
          image: c.image,
          imageAlt: c.imageAlt,
        }))}
      />
      <BookingBand />
    </>
  )
}

export default ConditionPage
